import { Clear } from '@mui/icons-material';
import { InputAdornment } from '@mui/material';
import { KeyboardEvent, RefObject, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetLocale } from '@legacy/components/global/LocaleProvider';
import { IconButton, TextField } from '@legacy/components/mui-components';
import {
  convertNumber,
  formatNumber,
  validateNumberInput,
} from '@legacy/utils/number';
import { IHours } from './HoursInput.types';
import { useLocaleStore } from '@/stores/locale';

interface IHoursInput {
  error: string;
  label: string;
  onChange: (props: IHours) => void;
  setError: (error: string) => void;
  value: string;
  /**
   * Overrides input clear button action.
   */
  clearAction?: () => void;
  inputRef?: RefObject<HTMLInputElement | null>;
  noDecimal?: boolean;
  onBlur?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  required?: boolean;
}

export const HoursInput = ({
  clearAction,
  error,
  inputRef,
  label,
  noDecimal,
  onBlur,
  onChange,
  onKeyDown,
  required,
  setError,
  value,
}: IHoursInput) => {
  const { t } = useTranslation('assignFlow');
  const { lang: siteLocale } = useLocaleStore();

  const textFieldOnBlur = () => {
    const { status, value: v } = validateNumberInput(
      value,
      t('DecimalFormatValidation'),
    );
    setError(status === 'error' ? v : '');
    onBlur?.();
  };

  const hoursOnChange = useCallback(
    (h: string) => {
      const { status, value: v } = validateNumberInput(
        h,
        t('DecimalFormatValidation'),
      );
      if (noDecimal) {
        if (status === 'error') {
          return;
        }
        onChange?.({
          hours: h,
          hoursInt: convertNumber(h, siteLocale),
          hoursStr: h,
        });
        return;
      }
      const decimalFormat = formatNumber(v, siteLocale);
      onChange?.({
        hours: h,
        hoursInt: convertNumber(decimalFormat, siteLocale),
        hoursStr: status === 'error' ? h : decimalFormat,
      });
    },
    [noDecimal, onChange, siteLocale, t],
  );

  useEffect(() => {
    hoursOnChange(value);
  }, [value, hoursOnChange]);

  return (
    <TextField
      ariaLabel={label}
      data-automation-id="Hours"
      error={!!error}
      errorMessage={error}
      slotProps={{
        htmlInput: {
          autoFocus: true,
        },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              {value ? (
                <IconButton
                  data-automation-id="HoursInputClearButton"
                  onClick={() => {
                    if (clearAction) {
                      clearAction();
                      return;
                    }
                    hoursOnChange('');
                    setError('');
                  }}
                  size="small"
                  title={t('ClearHoursButtonText')}
                >
                  <Clear fontSize="small" />
                </IconButton>
              ) : null}
            </InputAdornment>
          ),
        },
      }}
      inputRef={inputRef}
      label={label}
      onBlur={textFieldOnBlur}
      onChange={(e) => hoursOnChange(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder={formatNumber(0, siteLocale)}
      required={required}
      size="small"
      status={error ? 'error' : 'info'}
      sx={{ width: 'calc((100% - 23px) / 2)' }}
      usePopOutValidation
      value={value}
    />
  );
};
